import React from 'react';
import MathJaxHTMLContent from '@common/MathJaxHTMLContent';
import CircularLoader from '@common/CircularLoader';
import Alert from "@common/Alert";
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import questionsTheme from './questionsTheme';

const ExplanationPanel = ({
                              questionId,
                              isAnswered,
                              showExplanation,
                              explanations,
                              loadingExplanation,
                              currentExplanationIndex,
                              feedbackStatus,
                              onToggleExplanation,
                              onSetExplanationIndex,
                              onFeedback,
                          }) => {
    const explanationPages = explanations[questionId] || [];
    const activeIndex = currentExplanationIndex[questionId] || 0;
    const isVisible = showExplanation[questionId];
    const isLoading = loadingExplanation[questionId];
    const currentStatus = feedbackStatus[questionId];
    const currentExplanation = explanationPages[activeIndex];

    const feedbackExplanationId =
        currentExplanation?.explanationId ??
        explanationPages[explanationPages.length - 1]?.explanationId;

    const handleToggle = () => {
        if (!isLoading && isAnswered) onToggleExplanation(questionId);
    };

    // --- Sub-Renderers for cleaner architecture ---

    const renderLockedState = () => (
        <div className="ch-explanation-state-box flexColumn flexAlignCentre">
            <div className="flexRow flexAlignCentre smallGap ch-text-bold margin-b--xs" style={{ color: questionsTheme.primaryTeal }}>
                <LockIcon className="ch-icon-sm" style={{ color: questionsTheme.primaryTeal }} />
                <span>Ready to learn?</span>
            </div>
            <p className="ch-text-small" style={{ maxWidth: '80%' }}>
                <strong>Submit your answer</strong> to unlock 3 different step-by-step explanations.
            </p>
        </div>
    );

    const renderReadyToRevealState = () => (
        <div className="ch-explanation-state-box flexColumn flexAlignCentre">
            <div className="flexRow flexAlignCentre smallGap ch-text-bold margin-b--xs" style={{ color: questionsTheme.primaryTeal }}>
                <CheckCircleIcon className="ch-icon-sm" style={{ color: questionsTheme.correctGreen }} />
                <span>Answer submitted!</span>
            </div>
            <p className="margin-b--m ch-text-small">
                Ready to see the explanations?
            </p>
            <button
                onClick={handleToggle}
                disabled={isLoading}
                className="ch-button ch-btn-medium"
                style={{ backgroundColor: questionsTheme.primaryOrange, color: '#fff', minWidth: '160px' }}
            >
                {isLoading ? <CircularLoader displayInElement={true} size={20} /> : 'Show Explanation'}
            </button>
        </div>
    );

    const renderFeedbackFooter = () => (
        <div className="flexRow flexWrap flexAlignCentre flexSpaceBetween ch-explanation-footer padding-t--m margin-t--m" style={{ borderTop: `1px solid ${questionsTheme.unmarkedGray}` }}>
            {currentStatus === 'submitting' ? (
                <div className="flexRow flexAlignCentre smallGap">
                    <CircularLoader size={20} displayInElement={true} />
                    <span className="ch-text-small text-muted">Saving feedback...</span>
                </div>
            ) : currentStatus === 'success' ? (
                <div className="flexRow flexAlignCentre smallGap ch-text-bold" style={{ color: questionsTheme.correctGreen }}>
                    <CheckCircleIcon className="ch-icon-sm" />
                    <span className="ch-text-small">Feedback received! Thank you.</span>
                </div>
            ) : (
                <div className="flexRow flexAlignCentre smallGap">
                    <span className="ch-text-small ch-text-bold margin-r--xs" style={{ color: questionsTheme.textDark }}>Was this helpful?</span>
                    <button
                        onClick={() => onFeedback(questionId, true, feedbackExplanationId)}
                        className="ch-button ch-button-icon-l ch-btn-thumb-up"
                        title="Yes, this was helpful"
                    >
                        <ThumbUpIcon className="ch-icon-sm" />
                    </button>
                    <button
                        onClick={() => onFeedback(questionId, false, feedbackExplanationId)}
                        className="ch-button ch-button-icon-l ch-btn-thumb-down"
                        title="No, I need a better explanation"
                    >
                        <ThumbDownIcon className="ch-icon-sm" />
                    </button>
                </div>
            )}
        </div>
    );

    // --- Main Render ---
    return (
        <div className="flex1 min-width-0 ch-explanation-wrapper">
            <div className="ch-card padding--m ch-light-bg ch-explanation-card flexColumn" style={{ height: '100%' }}>

                {/* Header */}
                <h3
                    className="ch-h3 smallGap flexAlignCentre margin-b--m ch-explanation-title"
                    style={{
                        color: questionsTheme.primaryTeal,
                    }}
                >
                    <LightbulbIcon className="ch-icon-sm" />
                    <span>Explanation</span>
                </h3>

                {/* Content Area */}
                <div style={{ flex: '1 0 auto' }}>
                    {!isVisible && (
                        <div className="flexColumn" style={{ gap: '16px' }}>
                            {isAnswered ? renderReadyToRevealState() : renderLockedState()}
                        </div>
                    )}

                    {isVisible && (
                        <>
                            {explanationPages.length > 0 ? (
                                <div className="flexColumn fade-in-animation">
                                    {/* Pagination Pills */}
                                    {explanationPages.length > 1 && (
                                        <div className="flexRow flexWrap smallGap margin-b--m">
                                            {explanationPages.map((_, idx) => (
                                                <button
                                                    key={idx}
                                                    onClick={() => onSetExplanationIndex(questionId, idx)}
                                                    className={`ch-button ch-text-bold ${activeIndex === idx ? 'ch-activated' : 'ch-btn-outline'}`}
                                                    style={{
                                                        borderRadius: '16px',
                                                        padding: '4px 12px',
                                                        backgroundColor: activeIndex === idx ? questionsTheme.primaryTeal : 'transparent',
                                                        color: activeIndex === idx ? '#fff' : questionsTheme.primaryTeal,
                                                        borderColor: questionsTheme.primaryTeal
                                                    }}
                                                >
                                                    Approach {idx + 1}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {/* Explanation Content */}
                                    <MathJaxHTMLContent
                                        className="ch-card padding--m ch-surface-bg margin-b--none ch-explanation-content"
                                        style={{ border: `1px solid ${questionsTheme.unmarkedGray}`, minHeight: '150px' }}
                                        html={currentExplanation?.explanationHtml || currentExplanation?.explanationText || 'No explanation available'}
                                    />
                                </div>
                            ) : (
                                <Alert type="info" title="No explanations available for this question." />
                            )}
                        </>
                    )}
                </div>

                {/* Footer fixed to bottom of card */}
                {isVisible && explanationPages.length > 0 && renderFeedbackFooter()}
            </div>
        </div>
    );
};

export default ExplanationPanel;